import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import Button from '@superfunnel/ui-core/v2/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@superfunnel/ui-core/v2/popover';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';
import { Xmark } from 'iconoir-react';

export type StatusFilter = 'all' | 'active' | 'completed' | 'readyToComplete';

interface ExperimentsFilters {
  status: StatusFilter;
}

interface Props {
  children: ReactNode;
  disabled?: boolean;
  filters: ExperimentsFilters;
  onFiltersChange: (filters: ExperimentsFilters) => void;
  onReset: () => void;
}

export default function ExperimentsFilterPopover({
  children,
  disabled,
  filters,
  onFiltersChange,
  onReset,
}: Props) {
  const [open, setOpen] = useState(false);

  const hasActiveFilters = useMemo(() => {
    return filters.status !== 'all';
  }, [filters]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-4" align="end">
        <div className="space-y-4">
          {/* Header */}
          <div className="border-base-200 flex w-full items-center justify-between gap-2 border-b pb-2">
            <div className="text-base-700 text-base leading-8 font-medium">
              Filters
            </div>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="micro"
                onClick={onReset}
                className="text-sm"
              >
                Reset all
              </Button>
            )}
            <div className="flex w-full justify-end">
              <Button
                size={'icon'}
                variant={'ghost'}
                onClick={() => setOpen(false)}
              >
                <Xmark className="text-base-900" />
              </Button>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-1.5">
            <h4 className="text-base-700 text-sm font-medium">Status</h4>
            <Tabs
              value={filters.status}
              onValueChange={(value) => {
                onFiltersChange({
                  ...filters,
                  status: value as StatusFilter,
                });
              }}
              className="w-full"
            >
              <TabsList className="w-full">
                <TabsTrigger value="all" className="flex-1">
                  All
                </TabsTrigger>
                <TabsTrigger value="active" className="flex-1">
                  Active
                </TabsTrigger>
                <TabsTrigger value="completed" className="flex-1">
                  Completed
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
